import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { myEngagements } from '../../data/mockData';
import { useOfferings } from '../../components/OfferingsProvider';
import { centerToast } from '../../components/CenterToast';

const ENGAGEMENT_HUBS = ['services', 'learn', 'volunteer', 'give'];

const STATE_ORDER = ['submitted', 'draft', 'viewed'];

const STATE_LABEL = {
  submitted: 'Submitted',
  draft: 'Draft',
  viewed: 'Viewed'
};

const STATE_CHIP = {
  submitted: 'bg-primary/10 text-primary',
  draft: 'bg-amber-100 text-amber-700',
  viewed: 'bg-muted text-muted-foreground'
};

const STATE_DOT = {
  submitted: 'bg-primary',
  draft: 'bg-amber-500',
  viewed: 'bg-muted-foreground/40'
};

const SUBMITTED_STATUSES = ['under review', 'approved', 'confirmed', 'accepted', 'completed', 'submitted', 'applied', 'enrolled', 'active', 'donated', 'paid'];
const DRAFT_STATUSES = ['draft', 'incomplete', 'in progress', 'started', 'not submitted'];

// The engagement statuses are free-text, so anything unrecognised is treated as
// an action the member actually took rather than dropped from the list.
const toActivityState = (status) => {
  const text = typeof status === 'string' ? status.trim().toLowerCase() : '';
  if (!text) return 'draft';
  const percent = text.match(/(\d+)\s*%/);
  if (percent) return Number(percent[1]) >= 100 ? 'submitted' : 'draft';
  if (DRAFT_STATUSES.some((s) => text.includes(s))) return 'draft';
  if (text.includes('viewed')) return 'viewed';
  if (SUBMITTED_STATUSES.some((s) => text.includes(s))) return 'submitted';
  return 'submitted';
};

const SectionHeading = ({ children }) => (
  <p className="px-4 pt-5 pb-2 text-[12px] uppercase tracking-wider font-semibold text-muted-foreground">
    {children}
  </p>
);

export default function Activity({ onOpenExploreItem, onNavigate }) {
  const {
    learnHubItems,
    volunteerHubItems,
    givingOptions,
    serviceHubItems,
    dialysisCentres,
    casketPackages
  } = useOfferings();

  const COLLECTIONS = {
    services: serviceHubItems,
    learn: learnHubItems,
    volunteer: volunteerHubItems,
    give: givingOptions,
    dialysis: dialysisCentres,
    casket: casketPackages
  };

  // Engagement ids carry an `app-`/`eng-` prefix and are sometimes shortened
  // ("eng-mdd"), so id and title are both tried before giving up.
  const resolveEngagementTarget = (hub, engagement) => {
    const base = String(engagement.id || '').replace(/^(app|eng)-/, '');
    const title = String(engagement.title || '').trim().toLowerCase();
    const hubs = [hub, ...Object.keys(COLLECTIONS).filter((k) => k !== hub)];
    for (const key of hubs) {
      const item = COLLECTIONS[key]?.find((i) => (
        (base && (i.id === base || String(i.id).startsWith(`${base}-`)))
        || (title && String(i.title || '').trim().toLowerCase() === title)
      ));
      if (item) return { type: key, item };
    }
    return null;
  };

  const engagementRows = ENGAGEMENT_HUBS.flatMap((hub) => (
    (myEngagements[hub] || []).map((e) => ({
      id: e.id,
      title: e.title,
      meta: e.meta || e.status,
      state: toActivityState(e.status),
      target: resolveEngagementTarget(hub, e)
    }))
  ));

  // View history is not tracked anywhere in this app. Until it is, two live hub
  // items the member has no engagement with stand in for "recently viewed".
  const resolvedIds = new Set(engagementRows.map((r) => r.target?.item?.id).filter(Boolean));
  const viewedRows = ENGAGEMENT_HUBS
    .flatMap((hub) => (COLLECTIONS[hub] || []).map((item) => ({ hub, item })))
    .filter(({ item }) => !resolvedIds.has(item.id))
    .slice(0, 2)
    .map(({ hub, item }) => ({
      id: item.id,
      title: item.title,
      meta: item.date || item.org || item.location,
      state: 'viewed',
      target: { type: hub, item }
    }));

  const rows = [...engagementRows, ...viewedRows];

  const openRow = (row) => {
    if (row.target) {
      onOpenExploreItem?.(row.target);
      return;
    }
    centerToast(row.title, '📋');
  };

  return (
    <div className="min-h-screen bg-background page-fade-in" data-testid="activity-page">
      {rows.length === 0 && (
        <div className="px-4 desktop-shell">
          <div className="text-center py-16 text-sm text-muted-foreground" data-testid="activity-empty-state">
            <p className="font-semibold text-foreground">Nothing here yet</p>
            <p className="mt-1">Apply, enrol, volunteer or give and it will show up here.</p>
            <button
              type="button"
              className="mt-4 text-sm font-semibold text-primary"
              onClick={() => onNavigate?.('explore')}
            >
              Explore IhsanOne →
            </button>
          </div>
        </div>
      )}

      {STATE_ORDER.map((state) => {
        const sectionRows = rows.filter((r) => r.state === state);
        if (sectionRows.length === 0) return null;
        return (
          <section key={state} className="px-4 desktop-shell" data-testid={`activity-section-${state}`}>
            <SectionHeading>{STATE_LABEL[state]}</SectionHeading>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-border">
                {sectionRows.map((row) => (
                  <li key={`${state}-${row.id}`}>
                    <button
                      type="button"
                      data-testid={`activity-row-${row.id}`}
                      onClick={() => openRow(row)}
                      className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-muted/50 transition-colors"
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATE_DOT[state]}`} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold text-[#0F172A] truncate">{row.title}</span>
                        {row.meta && (
                          <span className="block text-xs text-muted-foreground mt-0.5 truncate">{row.meta}</span>
                        )}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${STATE_CHIP[state]}`}>
                        {STATE_LABEL[state]}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        );
      })}

      <div className="pb-6" />
    </div>
  );
}
